// oxlint-disable no-unused-vars
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { usePluginspace } from '../hooks/usePluginspace';
import api from '../services/api';
import CryptoJS from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';

declare global {
  interface Window {
    csdp?: {
      q?: any[];
      (...args: any[]): void;
    };
    // Adicionar funções de debug ao window
    clearSaleDebug?: {
      checkStatus: () => any;
      forceExecution: (sessionId?: string, appId?: string) => void;
      isConfigurationValid: () => boolean;
      getCurrentSessionId: () => Promise<string | null>;
      generateNewSessionId: () => Promise<string | null>;
    };
  }
}

interface ClearSaleData {
  sessionId: string;
  appId: string;
  pluginspaceId: number;
}

interface ClearSaleContextData {
  sessionId: string | null;
  appId: string | null;
  loading: boolean;
  error: string | null;
  initializeClearSale: () => Promise<void>;
  clearClearSaleData: () => void;
  getCurrentSessionId: () => Promise<string | null>;
  generateNewSessionId: () => Promise<string | null>;
  // Adicionar funções de debug
  checkClearSaleStatus: () => any;
  forceClearSaleExecution: (sessionId?: string, appId?: string) => void;
  isConfigurationValid: () => boolean;
}

interface ClearSaleProviderProps {
  children: ReactNode;
}

const ClearSaleContext = createContext<ClearSaleContextData>({} as ClearSaleContextData);

const Base64 = {
  encode: (str: string): string => {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => {
      return String.fromCharCode(parseInt(p1, 16));
    }));
  },
  decode: (str: string): string => {
    return decodeURIComponent(Array.prototype.map.call(atob(str), (c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }
};

export function ClearSaleProvider({ children }: ClearSaleProviderProps) {
  //console.log('[ClearSale] PROVIDER INICIANDO - Timestamp:', new Date().toISOString());
  
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [configurationValid, setConfigurationValid] = useState<boolean | null>(null);
  
  /* console.log('[ClearSale] Estados iniciais:', {
    sessionId,
    appId,
    loading,
    error,
    initialized,
    configurationValid
  }); */
  
  // Refs para evitar dependências desnecessárias no useCallback
  const initializedRef = useRef(false);
  const loadingRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const configCheckedRef = useRef(false);
  const appIdRef = useRef<string | null>(null);
  
  const { getToken, pluginspace } = usePluginspace();
  
  /* console.log('[ClearSale] usePluginspace resultado:', {
    getToken: typeof getToken,
    pluginspace: !!pluginspace,
    pluginspaceId: pluginspace?.id,
    pluginspaceName: pluginspace?.name
  }); */

  // Verificar configuração das chaves na inicialização
  useEffect(() => {
    const secretKey = process.env.REACT_APP_ENCRYPTION_KEY_CLEAR;
    const salt = process.env.REACT_APP_SALT_CLEAR;
    
    if (!secretKey || !salt) {
      /* console.log('\n =============== CLEARSALE CONFIGURAÇÃO ===============');
      console.log('  ClearSale está DESABILITADO - Chaves não configuradas');
      console.log('============================================================\n'); */
      setConfigurationValid(false);
      configCheckedRef.current = true;
      setError('ClearSale desabilitado - configuração ausente');
    } else {
      /* console.log('\n =============== CLEARSALE CONFIGURAÇÃO ===============');
      console.log(' Configuração válida - ClearSale habilitado');
      console.log('============================================================\n'); */
      setConfigurationValid(true);
      configCheckedRef.current = true;
    }
  }, []);

  // Sincronizar refs com estados
  useEffect(() => {
    initializedRef.current = initialized;
  }, [initialized]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
    //console.log('[CS] sessionId mudou para:', sessionId ? sessionId.substring(0, 10) + '...' : 'null');
    //console.log('[CS] sessionId atual:', { exists: !!sessionId, length: sessionId?.length, type: typeof sessionId });
  }, [sessionId]);

  // Adicionar sincronização do appId
  useEffect(() => {
    appIdRef.current = appId;
  }, [appId]);

  // Adicionar função para verificar se a configuração é válida
  const isConfigurationValid = useCallback(() => {
    return configurationValid === true;
  }, [configurationValid]);

  const getStorageKey = useCallback(() => {
    const pluginspaceId = pluginspace?.id ||
      (() => {
        try {
          const storedPluginspace = localStorage.getItem("this_pg");
          if (storedPluginspace) {
            const decoded = window.atob(storedPluginspace);
            const parsedData = JSON.parse(decoded);
            return parsedData.id;
          }
        } catch (e) {
          console.error('[CS] Erro ao obter pluginspace:', e);
        }
        return 'default';
      })();

    return `cs_encryptedData_${pluginspaceId}`;
  }, [pluginspace]);

  const decryptData = useCallback(async (encryptedData: string): Promise<ClearSaleData | null> => {
    try {
      const secretKey = process.env.REACT_APP_ENCRYPTION_KEY_CLEAR;
      const salt = process.env.REACT_APP_SALT_CLEAR;

      if (!secretKey || !salt ) {
        console.warn('[CS] Chaves de ambiente não configuradas. ClearSale será desabilitado.');
        return null;
      }

      // Tentativa 1: Abordagem padrão
      try {
        const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
        const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
        
        if (decryptedString) {
          const parsedData = JSON.parse(decryptedString);
          const decodedString = Base64.decode(parsedData);
          const deofuscatedString = decodedString.slice(0, -salt.length);
          const finalData = JSON.parse(deofuscatedString);
          return finalData;
        }
      } catch {
        // Continua para próxima tentativa
      }
      
      // Tentativa 2: Com WordArray
      try {
        const keyWordArray = CryptoJS.enc.Utf8.parse(secretKey);
        const bytes = CryptoJS.AES.decrypt(encryptedData, keyWordArray);
        const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
        
        if (decryptedString) {
          const parsedData = JSON.parse(decryptedString);
          const decodedString = Base64.decode(parsedData);
          const deofuscatedString = decodedString.slice(0, -salt.length);
          const finalData = JSON.parse(deofuscatedString);
          return finalData;
        }
      } catch {
        // Continua para próxima tentativa
      }
      
      // Tentativa 3: Com modo e padding explícitos
      try {
        const keyWordArray = CryptoJS.enc.Utf8.parse(secretKey);
        const bytes = CryptoJS.AES.decrypt(encryptedData, keyWordArray, {
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
        });
        const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
        
        if (decryptedString) {
          const parsedData = JSON.parse(decryptedString);
          const decodedString = Base64.decode(parsedData);
          const deofuscatedString = decodedString.slice(0, -salt.length);
          const finalData = JSON.parse(deofuscatedString);
          return finalData;
        }
      } catch {
        // Continua para próxima tentativa
      }
      
      // Tentativa 4: Verificar se já é JSON válido
      try {
        const directParse = JSON.parse(encryptedData);
        if (directParse && directParse.sessionId && directParse.appId) {
          return directParse;
        }
      } catch {
        // Não é JSON válido
      }
      
      // Tentativa 5: Se estiver em Base64
      try {
        const base64Decoded = Base64.decode(encryptedData);
        const jsonParsed = JSON.parse(base64Decoded);
        if (jsonParsed && jsonParsed.sessionId && jsonParsed.appId) {
          return jsonParsed;
        }
      } catch {
        // Não é Base64 válido
      }

      //console.error('[CS] Todas as abordagens de descriptografia falharam');
      return null;
    } catch (error) {
      console.error('[CS] Erro geral ao descriptografar dados', error);
      return null;
    }
  }, []);

 const initializeClearSaleScript = useCallback((sessionIdValue: string, appIdValue: string ) => {
  if (!appIdValue || !sessionIdValue) {
    console.warn('[CS] Valores inválidos para appId ou sessionId');
    return;
  }

  if (document.getElementById('clearsale-fp-script')) {
    console.log('[CS] Script ClearSale já carregado');
    return;
  }

  // Cria um script inline com o mesmo conteúdo do index.html
  const inlineScript = document.createElement('script');
  inlineScript.type = 'text/javascript';
  inlineScript.id = 'clearsale-init-script';

  const scriptContent = `
    (function (a, b, c, d, e, f, g) {
      a['CsdpObject'] = e; 
      a[e] = a[e] || function () {
        (a[e].q = a[e].q || []).push(arguments);
      }, 
      a[e].l = 1 * new Date(); 
      f = b.createElement(c),
      g = b.getElementsByTagName(c)[0]; 
      f.async = 1; 
      f.src = d; 
      g.parentNode.insertBefore(f, g);
    })(window, document, 'script', 'https://device.clearsale.com.br/p/fp.js', 'csdp');

    csdp('app', '${appIdValue}');
    csdp('sessionid', '${sessionIdValue}');
    window.clientData = {};
  `;

  inlineScript.text = scriptContent;
  document.head.appendChild(inlineScript);

  console.log('[CS] Script inline ClearSale injetado com appId e sessionId dinâmicos');
}, []);


  
  const checkClearSaleStatus = useCallback(() => {
    const status = {
      scriptLoaded: !!document.querySelector('script[src*="device.clearsale.com.br/p/fp.js"]'),
      csdpAvailable: typeof window.csdp === 'function',
      queueLength: window.csdp?.q?.length || 0,
      sessionId: sessionIdRef.current ? sessionIdRef.current.substring(0, 5) + '...' : 'null',
      appId: appIdRef.current ? appIdRef.current.substring(0, 5) + '...' : 'null',
      timestamp: new Date().toISOString()
    };
    
    console.log('[CS] Status atual do ClearSale:', status);
    
    // Verificar se existem elementos do ClearSale no DOM
    const clearSaleElements = document.querySelectorAll('[id*="clearsale"], [class*="clearsale"]');
    console.log('[CS] Elementos ClearSale no DOM:', clearSaleElements.length);
    
    return status;
  }, []);

  // Melhorar a função forceClearSaleExecution para usar parâmetros opcionais
  const forceClearSaleExecution = useCallback((sessionIdValue?: string, appIdValue?: string) => {
    const finalSessionId = sessionIdValue || sessionIdRef.current;
    const finalAppId = appIdValue || appIdRef.current;
    
    if (!finalSessionId || !finalAppId) {
      console.warn('[CS] Não é possível forçar execução sem sessionId e appId');
      return;
    }
    
    console.log('[CS] Forçando execução do ClearSale...', {
      sessionId: finalSessionId.substring(0, 5) + '...',
      appId: finalAppId.substring(0, 5) + '...'
    });
    
    // Múltiplas tentativas para garantir execução
    const executeCommands = () => {
      if (typeof window.csdp === 'function') {
        window.csdp('app', finalAppId);
        window.csdp('sessionid', finalSessionId);
        console.log('[CS] Comandos executados com sucesso');
        return true;
      }
      return false;
    };
    
    // Tentativa imediata
    if (executeCommands()) return;
    
    // Tentativas com delay
    setTimeout(executeCommands, 100);
    setTimeout(executeCommands, 500);
    setTimeout(executeCommands, 1000);
    
  }, []);

  const initializeClearSale = useCallback(async () => {
    console.log('[CS] Status atual do ClearSale:', {
      scriptLoaded: !!document.querySelector('script[src*="device.clearsale.com.br/p/fp.js"]'),
      csdpAvailable: typeof window.csdp === 'function',
      queueLength: window.csdp?.q?.length || 0,
      timestamp: new Date().toISOString()
    });
    

    console.log('[CS] initializeClearSale chamado - Estados atuais:', {
      initialized: initializedRef.current,
      sessionId: sessionIdRef.current ? sessionIdRef.current.substring(0, 5) + '...' : 'null',
      loading: loadingRef.current,
      configurationValid
    });

    // Verificar se a configuração é válida
    if (configurationValid === false) {
      console.log('[CS] Configuração inválida, ClearSale desabilitado');
      return;
    }

    // Se ainda não foi verificada a configuração, aguardar
    if (configurationValid === null) {
      console.log('[CS] Aguardando verificação de configuração...');
      return;
    }

    // Se já foi inicializado e temos sessionId, não fazer nada
    if (initializedRef.current && sessionIdRef.current) {
      console.log('[CS] ClearSale já inicializado, usando dados existentes');
      return;
    }

    // Se já está carregando, não fazer nada
    if (loadingRef.current) {
      console.log('[CS] ClearSale já está sendo carregado...');
      return;
    }

    try {
      console.log('[CS] Iniciando processo de inicialização...');
      setLoading(true);
      loadingRef.current = true;
      setError(null);
      
      // 1. Verificar se temos dados criptografados no sessionStorage
      const storageKey = getStorageKey();
      //console.log('[CS] StorageKey gerada:', storageKey);
      const storedEncryptedData = sessionStorage.getItem(storageKey);
      
      if (storedEncryptedData) {
        //console.log('[CS] Dados criptografados encontrados no storage, tamanho:', storedEncryptedData.length);

        // 2. Descriptografar os dados armazenados
        const decryptedData = await decryptData(storedEncryptedData);
        
        if (decryptedData && decryptedData.sessionId && decryptedData.appId) {
          console.log('[CS] Dados descriptografados com sucesso do storage');

          // 3. Atualizar estados (refs primeiro para garantir sincronização)
          sessionIdRef.current = decryptedData.sessionId;
          appIdRef.current = decryptedData.appId; 
          initializedRef.current = true;
          
          setSessionId(decryptedData.sessionId);
          setAppId(decryptedData.appId);
          setInitialized(true);

          // 4. Inicializar script do ClearSale
          initializeClearSaleScript(decryptedData.sessionId, decryptedData.appId);
          
          setLoading(false);
          loadingRef.current = false;
          return;
        } else {
          console.warn('[CS] Dados armazenados inválidos ou expirados');
        }
      } else {
        console.log('[CS] Nenhum dado encontrado no storage, buscando do backend...');
      }
      
      // 5. Buscar novos dados do backend se não tiver no storage ou se os dados forem inválidos
      const token = await getToken();
      
      if (!token || typeof token !== 'string') {
        setError('Token inválido');
        setLoading(false);
        loadingRef.current = false;
        console.warn('[CS] Token inválido');
        return;
      }

      //console.log('[CS] Buscando dados do ClearSale no backend...');
      const response = await api.get('/api/clearsale/getClearSaleData', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const res = response.data;
      if (!res || !res?.success || !res?.data || !res?.data?.sensitiveData) {
        setError('Resposta da API não indica sucesso');
        setLoading(false);
        loadingRef.current = false;
        //console.error('[CS] Resposta da API não indica sucesso');
        return;
      }

      // 6. Extrair os dados criptografados
      const encryptedData = typeof res.data.sensitiveData === 'string' 
        ? res.data.sensitiveData 
        : res.data.sensitiveData.encryptedData;
      
      if (!encryptedData) {
        setError('Dados criptografados não encontrados');
        setLoading(false);
        loadingRef.current = false;
        console.error('[CS] Dados criptografados não encontrados');
        return;
      }
      
      // 7. Armazenar os dados criptografados no sessionStorage
      sessionStorage.setItem(storageKey, encryptedData);
      console.log('[CS] Dados criptografados armazenados no storage');

      // 8. Descriptografar os dados obtidos
      const decryptedData = await decryptData(encryptedData);

      if (!decryptedData) {
        setError('Falha ao descriptografar dados');
        setLoading(false);
        loadingRef.current = false;
        console.error('[CS] Falha ao descriptografar dados');
        return;
      }

      if (!decryptedData.sessionId || !decryptedData.appId) {
        setError('Dados obrigatórios ausentes nos dados descriptografados');
        setLoading(false);
        loadingRef.current = false;
        console.error('[CS] Dados obrigatórios ausentes');
        return;
      }
      
      // 9. Atualizar estados
      console.log('[CS] Atualizando estados com dados do backend');
      
      // Atualizar os refs primeiro para garantir sincronização
      sessionIdRef.current = decryptedData.sessionId;
      appIdRef.current = decryptedData.appId;
      initializedRef.current = true;
      
      setSessionId(decryptedData.sessionId);
      setAppId(decryptedData.appId);
      setInitialized(true);
      
      // 10. Inicializar script do ClearSale
      initializeClearSaleScript(decryptedData.sessionId, decryptedData.appId);
      
      setLoading(false);
      loadingRef.current = false;
      
      console.log('[CS] ClearSale inicializado com sucesso!');
      
    } catch (error: any) {
      setError(error?.message || 'Erro desconhecido');
      setLoading(false);
      loadingRef.current = false;
      console.error('[CS] Erro ao carregar dados:', error);
    }
  }, [getStorageKey, getToken, decryptData, configurationValid]);

  const clearClearSaleData = useCallback(() => {
    setSessionId(null);
    sessionIdRef.current = null;
    setAppId(null);
    setInitialized(false);
    initializedRef.current = false;
    setError(null);
    
    // Limpar storage
    const storageKey = getStorageKey();
    sessionStorage.removeItem(storageKey);
    
    console.log('[CS] Dados do ClearSale limpos');
  }, [getStorageKey]);

  // Melhorar o generateNewSessionId com melhor validação
  const generateNewSessionId = useCallback(async (): Promise<string | null> => {
    //console.log('[CS] ===== GERANDO NOVO SESSIONID PARA PEDIDO =====');
    //console.log('[CS] Timestamp:', new Date().toISOString());

    // Verificar se a configuração é válida
    if (configurationValid === false) {
      console.log('[CS] Configuração inválida, não é possível gerar novo sessionId');
      return null;
    }

    if (configurationValid === null) {
      console.log('[CS] Aguardando verificação de configuração...');
      // Aguardar um pouco para a configuração ser verificada
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (configurationValid === null) {
        console.log('[CS] Timeout na verificação de configuração');
        return null;
      }
    }

    // Se não temos appId, precisamos inicializar primeiro
    if (!appIdRef.current) {
      console.log('[CS] AppId não disponível, inicializando ClearSale primeiro...');
      try {
        await initializeClearSale();
        
        // Aguardar a inicialização
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        if (!appIdRef.current) {
          console.error('[CS] Falha ao obter appId mesmo após inicialização');
          return null;
        }
      } catch (error) {
        console.error('[CS] Erro durante inicialização:', error);
        return null;
      }
    }

    try {
      // Gerar um novo sessionId único usando UUID
      const newSessionId = uuidv4();
      
      console.log('[CS] Novo sessionId gerado:', {
        newSessionId: newSessionId.substring(0, 5) + '...',
        appId: appIdRef.current!.substring(0, 5) + '...',
        oldSessionId: sessionIdRef.current ? sessionIdRef.current.substring(0, 5) + '...' : 'null'
      });

      // Atualizar os estados e refs com o novo sessionId
      sessionIdRef.current = newSessionId;
      setSessionId(newSessionId);

      // Re-executar o script da ClearSale com o novo sessionId
      console.log('[CS] Re-executando script ClearSale com NOVO sessionId...');
      initializeClearSaleScript(newSessionId, appIdRef.current!);
      
      // Aguardar para garantir que o script foi executado
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Verificar se o script foi executado corretamente
      const status = checkClearSaleStatus();
      console.log('[CS] Status após re-execução:', status);

      //console.log('[CS] ===== NOVO SESSIONID CONFIGURADO COM SUCESSO =====');
      console.log('[CS] SessionId:', newSessionId.substring(0, 5) + '...');
      //console.log('[CS] ================================================');
      
      return newSessionId;
      
    } catch (error: any) {
      console.error('[CS] Erro ao gerar novo sessionId:', error);
      setError(error?.message || 'Erro ao gerar novo sessionId');
      return null;
    }
  }, [configurationValid, initializeClearSale, checkClearSaleStatus]);


  // Função helper para obter sessionId sempre atualizado
  const getCurrentSessionId = useCallback(async (): Promise<string | null> => {
    console.log('[CS] getCurrentSessionId chamado');
    
    // Primeiro tenta usar o estado atual
    if (sessionIdRef.current) {
      console.log('[CS] SessionId encontrado no ref:', sessionIdRef.current.substring(0, 5) + '...');
      return sessionIdRef.current;
    }
    
    // Se não tem no ref, tenta buscar do sessionStorage
    try {
      const storageKey = getStorageKey();
      const encryptedData = sessionStorage.getItem(storageKey);
      
      if (encryptedData) {
        console.log('[CS] Dados encontrados no sessionStorage, descriptografando...');
        const decryptedData = await decryptData(encryptedData);
        if (decryptedData && decryptedData.sessionId) {
          console.log('[CS] SessionId recuperado do storage:', decryptedData.sessionId.substring(0, 5) + '...');
          
          // Atualiza o estado com os dados encontrados
          setSessionId(decryptedData.sessionId);
          setAppId(decryptedData.appId);
          sessionIdRef.current = decryptedData.sessionId;
          
          return decryptedData.sessionId;
        }
      }
    } catch (error) {
      console.error('[CS] Erro ao recuperar sessionId do storage:', error);
    }
    
    console.log('[CS] SessionId não encontrado');
    return null;
  }, [getStorageKey, decryptData]);

  // Expor funções de debug globalmente
  useEffect(() => {
    window.clearSaleDebug = {
      checkStatus: checkClearSaleStatus,
      forceExecution: forceClearSaleExecution,
      isConfigurationValid: isConfigurationValid,
      getCurrentSessionId: getCurrentSessionId,
      generateNewSessionId: generateNewSessionId
    };

    // Limpar na desmontagem
    return () => {
      delete window.clearSaleDebug;
    };
  }, [checkClearSaleStatus, forceClearSaleExecution, isConfigurationValid, getCurrentSessionId, generateNewSessionId]);

  // Inicialização automática apenas quando o pluginspace estiver disponível e configuração for válida
  useEffect(() => {
    console.log('[CS] pluginspace - Estado atual:', {
      pluginspace: !!pluginspace,
      pluginspaceId: pluginspace?.id,
      initialized: initializedRef.current,
      loading: loadingRef.current,
      configurationValid,
      configChecked: configCheckedRef.current
    });
    
    if (pluginspace && !initializedRef.current && !loadingRef.current && configurationValid === true) {
      console.log('[CS] Pluginspace disponível e configuração válida, inicializando ClearSale automaticamente...');
      initializeClearSale();
    } else {
      console.log('[CS] Condições não atendidas para inicialização:', {
        hasPluginspace: !!pluginspace,
        alreadyInitialized: initializedRef.current,
        currentlyLoading: loadingRef.current,
        configurationValid
      });
    }
  }, [pluginspace, initializeClearSale, configurationValid]);

  const contextValue: ClearSaleContextData = {
    sessionId,
    appId,
    loading,
    error,
    initializeClearSale,
    clearClearSaleData,
    getCurrentSessionId,
    generateNewSessionId,
    // Adicionar as funções de debug
    checkClearSaleStatus,
    forceClearSaleExecution,
    isConfigurationValid,
  };

  return (
    <ClearSaleContext.Provider value={contextValue}>
      {children}
    </ClearSaleContext.Provider>
  );
}

export function useClearSale(): ClearSaleContextData {
  const context = useContext(ClearSaleContext);
  
  if (!context) {
    throw new Error('useClearSale deve ser usado dentro de um ClearSaleProvider');
  }
  
  return context;
}